import { useCallback, useRef, useState } from 'react';
import { Icon } from '../icons';
import type { MusicId } from '../../lib/types';

interface Props {
  value: MusicId | null;
  onChange: (m: MusicId) => void;
  onNext: () => void;
  onBack: () => void;
}

interface Track {
  id: MusicId;
  t: string;
  s: string;
  d: string;
  upload?: boolean;
}

const TRACKS: Track[] = [
  { id: 'shalom', t: 'שלום עליכם', s: 'מסורתי · רך', d: '2:14' },
  { id: 'simchot', t: 'שמחות החיים', s: 'משפחתי · חם', d: '1:48' },
  { id: 'derech', t: 'דרך ארוכה', s: 'נוסטלגי · אקוסטי', d: '2:32' },
  { id: 'shir', t: 'שיר של אהבה', s: 'רומנטי · פסנתר', d: '2:08' },
  { id: 'rikud', t: 'בוא נרקוד', s: 'קצבי · אנרגטי', d: '1:54' },
  { id: 'upload', t: 'מוזיקה משלכם', s: 'העלאת קובץ MP3', d: '—', upload: true },
];

/** Short preview tones (Hz) — distinct per row until licensed previews are hosted. */
const PREVIEW_FREQ: Record<Exclude<MusicId, 'upload'>, number> = {
  shalom: 392,
  simchot: 440,
  derech: 349,
  shir: 523,
  rikud: 587,
};

function Wave() {
  return (
    <div className="waveform">
      {[6, 12, 8, 16, 10, 14, 7, 11].map((h, i) => (
        <span key={i} style={{ height: h, animationDelay: `${i * 0.08}s` }} />
      ))}
    </div>
  );
}

export function MusicScreen({ value, onChange, onNext, onBack }: Props) {
  const ctxRef = useRef<AudioContext | null>(null);
  const stopPreviewRef = useRef<(() => void) | null>(null);
  const [previewingId, setPreviewingId] = useState<MusicId | null>(null);

  const stopPreview = useCallback(() => {
    stopPreviewRef.current?.();
    stopPreviewRef.current = null;
    setPreviewingId(null);
  }, []);

  const playPreview = useCallback(
    (id: MusicId, e: React.MouseEvent) => {
      e.stopPropagation();
      if (id === 'upload') return;
      stopPreview();

      const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioCtx) return;

      const ctx = ctxRef.current ?? new AudioCtx();
      ctxRef.current = ctx;
      if (ctx.state === 'suspended') void ctx.resume();

      const freq = PREVIEW_FREQ[id];
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.value = 0.07;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();

      const stop = () => {
        try {
          osc.stop();
        } catch {
          /* already stopped */
        }
        gain.disconnect();
        osc.disconnect();
      };

      const tid = window.setTimeout(() => {
        stop();
        setPreviewingId((cur) => (cur === id ? null : cur));
        stopPreviewRef.current = null;
      }, 550);

      stopPreviewRef.current = () => {
        window.clearTimeout(tid);
        stop();
        setPreviewingId(null);
      };
      setPreviewingId(id);
    },
    [stopPreview],
  );

  return (
    <div className="screen">
      <div className="screen-header">
        <button className="icon-btn" onClick={onBack}>
          <Icon.ChevronRight size={20} />
        </button>
        <div className="step-counter">שלב 4 מתוך 5</div>
        <div style={{ width: 38 }} />
      </div>
      <div className="screen-scroll">
        <div className="eyebrow" style={{ marginTop: 24 }}>
          פסקול
        </div>
        <h1 className="h1">בוחרים מוזיקה</h1>
        <p className="lede">לחצו ▶ לתצוגה מקדימה קצרה. הרצועות המלאות ישובצו ביצוא הסופי.</p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {TRACKS.map((tr) => (
            <div
              key={tr.id}
              className={'music-row' + (value === tr.id ? ' is-selected' : '')}
              onClick={() => {
                stopPreview();
                onChange(tr.id);
              }}
            >
              <button
                type="button"
                className="play-btn"
                aria-label={tr.upload ? 'העלאה' : 'תצוגה מקדימה'}
                onClick={(e) => {
                  if (tr.upload) {
                    e.stopPropagation();
                    return;
                  }
                  if (previewingId === tr.id) {
                    e.stopPropagation();
                    stopPreview();
                    return;
                  }
                  playPreview(tr.id, e);
                }}
              >
                {tr.upload ? (
                  <Icon.Plus size={18} />
                ) : previewingId === tr.id ? (
                  <Icon.Pause size={16} />
                ) : (
                  <Icon.Play size={16} />
                )}
              </button>
              <div className="info">
                <div className="t">{tr.t}</div>
                <div className="s">{tr.s}</div>
              </div>
              {value === tr.id && !tr.upload && <Wave />}
              <div className="dur">{tr.d}</div>
            </div>
          ))}
        </div>

        <div className="tip-row">
          <div className="tip-glyph">
            <Icon.Music size={18} />
          </div>
          <div>כל הרצועות בעלות רישיון מסחרי — ניתן לשתף בכל מקום ללא חשש.</div>
        </div>
      </div>
      <div className="cta-dock">
        <button className="btn-primary" disabled={!value} onClick={onNext}>
          יוצרים את הסרטון
          <Icon.Sparkles size={18} />
        </button>
      </div>
    </div>
  );
}
